/**
 * WIDGET MODE DETECTION & CONFIGURATION
 *
 * Ce module gère la détection et la configuration des modes app/widget
 */

import type { AppearanceConfig } from '@meilleurtaux/form-factory-ds'
import type { Config, WidgetConfig } from 'type/app'

/**
 * Récupère la configuration widget
 */
export function getWidgetConfig(): WidgetConfig | null {
  if (globalThis.window === undefined) return null

  // Configuration depuis window (intégration widget)
  const windowConfig = globalThis.window?.formFactoryConfig
  if (windowConfig?.client_id) {
    return windowConfig
  }

  return null
}

export function getAppearanceFromConfigs(
  config?: Config,
  apiConfig?: AppearanceConfig,
): AppearanceConfig | undefined {
  if (!config?.appearance) {
    return apiConfig
  }

  if (!apiConfig) {
    return {
      buttonType: config.appearance?.button_type,
      ctaLabel: config.appearance?.cta_label,
      ctaTextColor: config.appearance?.cta_text_color,
      stepperColor: config.appearance?.stepper_color,
      lastCtaLabel: config.appearance?.last_cta_label,
      mainColor: config.appearance?.main_color,
      textColor: config.appearance?.text_color,
      trustpilotBusinessId: config.appearance?.trustpilot_business_id,
      trustpilotToken: config.appearance?.trustpilot_token,
      trustpilotRedirectUrl: config.appearance?.trustpilot_redirect_url,
      displayTrustpilot: config.appearance?.display_trustpilot,
      logoUrl: config.appearance?.logo_url,
      faviconUrl: config.appearance?.favicon_url,
      fontFamily: config.appearance?.font_family,
      fontFamilyName: config.appearance?.font_family_name,
    }
  }

  const mergeValue = <T>(configValue: T | undefined, apiValue: T | undefined): T | undefined =>
    configValue ?? apiValue

  const appearance = config.appearance

  return {
    buttonType: mergeValue(appearance.button_type, apiConfig.buttonType),
    ctaLabel: mergeValue(appearance.cta_label, apiConfig.ctaLabel),
    ctaTextColor: mergeValue(appearance.cta_text_color, apiConfig.ctaTextColor),
    stepperColor: mergeValue(appearance.stepper_color, apiConfig.stepperColor),
    lastCtaLabel: mergeValue(appearance.last_cta_label, apiConfig.lastCtaLabel),
    mainColor: mergeValue(appearance.main_color, apiConfig.mainColor),
    textColor: mergeValue(appearance.text_color, apiConfig.textColor),
    displayTrustpilot: mergeValue(appearance.display_trustpilot, apiConfig.displayTrustpilot),
    trustpilotBusinessId: mergeValue(
      appearance.trustpilot_business_id,
      apiConfig.trustpilotBusinessId,
    ),
    trustpilotToken: mergeValue(appearance.trustpilot_token, apiConfig.trustpilotToken),
    trustpilotRedirectUrl: mergeValue(
      appearance.trustpilot_redirect_url,
      apiConfig.trustpilotRedirectUrl,
    ),
    logoUrl: mergeValue(appearance.logo_url, apiConfig.logoUrl),
    faviconUrl: mergeValue(appearance.favicon_url, apiConfig.faviconUrl),
    fontFamily: mergeValue(appearance.font_family, apiConfig.fontFamily),
    fontFamilyName: mergeValue(appearance.font_family_name, apiConfig.fontFamilyName),
  }
}

// Augmentation des types globaux
declare global {
  var formFactory:
    | {
        /** Met à jour le consentement TagCommander à la volée, sans rechargement du widget. */
        updateConsent: (status: 'all-on' | 'all-off') => void
      }
    | undefined
  interface Window {
    formFactoryConfig?: WidgetConfig
    formFactory?: {
      /** Met à jour le consentement TagCommander à la volée, sans rechargement du widget. */
      updateConsent: (status: 'all-on' | 'all-off') => void
    }
  }
}
